import { type Tier, type Feature, TIER_LABEL, canMapFor } from './featureTiers';
import {
  DEFAULT_UAE_LIMITS,
  isUaePartnerPath,
  uaeLevelTier,
  uaeProgrammeCanMap,
  uaeLimitsFor,
  uaeToolLevel,
  type UaeAccessLevelValue,
  type UaeLimitsConfig,
} from '~/utils/uae/accessLevel';

export type SubscriptionPlan = 'MEMBER' | 'PRO' | 'TEAM';
export type SubscriptionInterval = 'MONTHLY' | 'ANNUAL';

export const FREE_CHAT_LIMIT = 20;
export const FREE_MARKETING_LIMIT = 4;
const ACTIVE_STATUSES = new Set(['active', 'trialing']);

export type EntitlementInput = {
  subscriptionPlan?: SubscriptionPlan | null;
  subscriptionInterval?: SubscriptionInterval | null;
  subscriptionStatus?: string | null;
  masterclassPaidAt?: Date | string | null;
  legacyUnlimitedTools?: boolean | null;
  legacyProGrant?: boolean | null;
  teamSeatActive?: boolean | null;
  // ID-181: seat in a live B2B agency workspace; the agency pays, so it grants Team (Pro) access like a team seat.
  b2bSeatActive?: boolean | null;
  // Comped Member — existing free users + one-time masterclass buyers migrated
  // into Member at no charge (Epic B / B7). Resolves to MEMBER access without a
  // Stripe subscription. Set by the tier migration script.
  compedMemberGrant?: boolean | null;
  // UAE agents (AgentProfile.market = UAE) are gated by access level, not billing (ID-106).
  market?: string | null;
  uaeAccessLevel?: string | null;
  uaeLimits?: UaeLimitsConfig | null;
  // 'partner' keeps the per-level UAE limits; every other UAE agent gets Pro access (owner, 30 Sep 2026).
  signupPath?: string | null;
  // Emirates ID approved + agreement signed: tools open as Joined (uaeToolLevel).
  uaeLive?: boolean | null;
};

export type Entitlements = {
  hasFullMasterclass: boolean;
  unlimitedTools: boolean;
  fastPayoutEveryDeal: boolean;
  freeChatLimit: number;
  freeMarketingLimit: number;
  // Legacy display label, kept byte-compatible with the pre-Epic-B UI
  // (PricingModal/PlanChip switch on 'Basic plan' | 'Pro' | 'Team'). New surfaces
  // should read `tierLabel` instead.
  planLabel: 'Basic plan' | 'Member' | 'Pro' | 'Team' | 'UAE agent';
  // Set for UAE agents only; null for everyone on subscription gating.
  uaeAccessLevel: UaeAccessLevelValue | null;
  // ── Epic B additive fields (consumed by the new gating layer) ──
  // Access tier (Preview/Member/Pro). Team plans resolve to PRO access.
  tier: Tier;
  // Canonical display label: Preview / Member / Pro / Team.
  tierLabel: string;
  // Per-feature capability map derived from the tier + FEATURE_MIN_TIER.
  can: Record<Feature, boolean>;
};

// Resolve the ACCESS tier from billing state. Distinct from the label: a Team
// plan (and team seats) grant PRO access. masterclassPaidAt does NOT grant a
// tier on its own — those buyers are comped to Member explicitly by the B7
// migration (compedMemberGrant), so the engine reflects granted state only.
const resolveTier = (x: EntitlementInput, isActivePaid: boolean): Tier => {
  if (isActivePaid) {
    if (x.subscriptionPlan === 'PRO' || x.subscriptionPlan === 'TEAM') return 'PRO';
    if (x.subscriptionPlan === 'MEMBER') return 'MEMBER';
  }
  if (x.legacyProGrant || x.teamSeatActive || x.b2bSeatActive) return 'PRO';
  if (x.compedMemberGrant) return 'MEMBER';
  return 'PREVIEW';
};

// UAE programme agents: Pro access (the service fee covers it); Pro features unlock once the Emirates ID is verified.
const resolveUaeProgrammeEntitlements = (level: UaeAccessLevelValue, config: UaeLimitsConfig): Entitlements => {
  const limits = uaeLimitsFor(level, config);
  return {
    hasFullMasterclass: false,
    unlimitedTools: level !== 'SIGNED_UP',
    fastPayoutEveryDeal: false,
    freeChatLimit: limits?.aiQuestions ?? FREE_CHAT_LIMIT,
    freeMarketingLimit: limits?.marketingPosts ?? FREE_MARKETING_LIMIT,
    planLabel: 'UAE agent',
    uaeAccessLevel: level,
    tier: 'PRO',
    tierLabel: TIER_LABEL.PRO,
    can: uaeProgrammeCanMap(level),
  };
};

// UAE partner path: tier from the access level, UAE monthly limits, billing state ignored.
const resolveUaeEntitlements = (level: UaeAccessLevelValue, config: UaeLimitsConfig): Entitlements => {
  const tier = uaeLevelTier(level);
  const limits = uaeLimitsFor(level, config, 'partner');
  return {
    hasFullMasterclass: false,
    unlimitedTools: tier === 'PRO',
    fastPayoutEveryDeal: false,
    freeChatLimit: limits?.aiQuestions ?? FREE_CHAT_LIMIT,
    freeMarketingLimit: limits?.marketingPosts ?? FREE_MARKETING_LIMIT,
    planLabel: 'UAE agent',
    uaeAccessLevel: level,
    tier,
    tierLabel: TIER_LABEL[tier],
    can: canMapFor(tier),
  };
};

export function resolveEntitlements(x: EntitlementInput): Entitlements {
  // An agency seat is paid by the agency, so the personal UAE gating does not apply to it.
  const uaeLevel = x.b2bSeatActive ? null : uaeToolLevel(x);
  if (uaeLevel && !isUaePartnerPath(x.signupPath)) {
    return resolveUaeProgrammeEntitlements(uaeLevel, x.uaeLimits ?? DEFAULT_UAE_LIMITS);
  }
  if (uaeLevel) return resolveUaeEntitlements(uaeLevel, x.uaeLimits ?? DEFAULT_UAE_LIMITS);

  const isActivePaid = !!x.subscriptionPlan && ACTIVE_STATUSES.has(String(x.subscriptionStatus ?? ''));
  const isAnnualPaid = isActivePaid && x.subscriptionInterval === 'ANNUAL';
  const onActiveTeamSeat = !!x.teamSeatActive || !!x.b2bSeatActive;
  const isLegacyPro = !!x.legacyProGrant;
  const hasMasterclassOneTime = !!x.masterclassPaidAt;

  const tier = resolveTier(x, isActivePaid);
  const isProTier = tier === 'PRO';

  // Comped Members and comped Pro get the full course like an annual plan (coordinator decision).
  const hasFullMasterclass =
    hasMasterclassOneTime || isAnnualPaid || onActiveTeamSeat || !!x.compedMemberGrant || isLegacyPro;
  // unlimitedTools / fastPayout are now tier-driven. This reproduces the prior
  // behaviour for every currently-possible input (PRO/TEAM/legacy/team-seat all
  // resolve to PRO access; free stays PREVIEW) while ensuring a new MEMBER stays
  // capped rather than inheriting Pro perks.
  const unlimitedTools = isProTier || !!x.legacyUnlimitedTools;
  // FastPay (owner answer 14): Pro access only — Pro, Team, team seats, legacy Pro.
  const fastPayoutEveryDeal = isProTier;

  // Legacy label — unchanged values, plus MEMBER so a Member isn't mislabelled.
  const planLabel: Entitlements['planLabel'] = isActivePaid
    ? x.subscriptionPlan === 'TEAM'
      ? 'Team'
      : x.subscriptionPlan === 'MEMBER'
        ? 'Member'
        : 'Pro'
    : onActiveTeamSeat
      ? 'Team'
      : isLegacyPro
        ? 'Pro'
        : 'Basic plan';

  // Canonical label: Team plans/seats present as "Team" despite PRO access.
  const isTeam = (isActivePaid && x.subscriptionPlan === 'TEAM') || onActiveTeamSeat;
  const tierLabel = isTeam ? TIER_LABEL.TEAM : TIER_LABEL[tier];

  return {
    hasFullMasterclass,
    unlimitedTools,
    fastPayoutEveryDeal,
    freeChatLimit: FREE_CHAT_LIMIT,
    freeMarketingLimit: FREE_MARKETING_LIMIT,
    planLabel,
    uaeAccessLevel: null,
    tier,
    tierLabel,
    can: canMapFor(tier),
  };
}
